import './styles.scoped.css'
import {useEffect} from 'react'
import {Typography, Button, IconButton, Modal, TextField, Avatar, Chip} from '@mui/material'
import {Close, DateRange, AccessTime} from '@mui/icons-material'
import {Controller, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {useMobile} from '../../hooks'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime} from '../../utils/helpers'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {format} from 'date-fns'
import {scheduleApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {VisitDialogCardSkeleton} from '../Skeletons'

export default ({open, setOpen, loadData, items = [], loading = false}) => {
    const {mobile} = useMobile()
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                items: yup.array().of(
                    yup.object().shape({
                        id: yup.string(),
                        date: yup.date(),
                        start_time: yup.string(),
                        end_time: yup.string(),
                    }),
                ),
            }),
        ),
    })

    useEffect(() => {
        if (items?.length > 0) {
            form.setValue(
                'items',
                items?.map(item => ({...item, date: new Date(ignoreTimezone(item?.date))})),
            )
        }
    }, [items])

    const submit = data => {
        setLoading(true)
        scheduleApi
            .rescheduleVisits(branchId, items?.[0]?.schedule_id, {
                visits: data?.items?.map(item => ({
                    visit_id: item?.id,
                    date: format(new Date(item?.date), 'yyyy-MM-dd'),
                    start_time: item?.start_time?.slice(0, 5),
                    end_time: item?.end_time?.slice(0, 5),
                })),
            })
            .then(() => {
                setLoading(false)
                setOpen(false)
                if (items?.length == 1) {
                    setMessageBox({
                        status: 'success',
                        title: 'Schedule Rescheduled!',
                        description: 'The caregiver and client have been notified of the updated schedule.',
                    })
                } else if (items?.length > 1) {
                    setMessageBox({
                        status: 'success',
                        title: `${items?.length} Schedules Rescheduled!`,
                        description: 'The caregiver and client have been notified of the updated schedule.',
                    })
                }
                loadData()
            })
            .catch(error => {
                setLoading(false)
                setOpen(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    Reschedule
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" onClick={() => setOpen(false)} style={{flex: 1}}>
                                    Cancel
                                </Button>
                                <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)} disabled={loading} style={{flex: 1}}>
                                    Reschedule {items?.length > 1 ? `(${items?.length})` : ''}
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            Reschedule
                        </Typography>
                    )}
                    <br />
                    <Typography variant="body2" color="text.primary" className="title">
                        Select a new date and time for this visit{items?.length > 1 ? 's' : ''}.
                    </Typography>
                    {items.map((item, index) =>
                        loading ? (
                            <div key={index}>
                                <br />
                                <VisitDialogCardSkeleton section="reportTime" />
                            </div>
                        ) : (
                            <div key={index}>
                                <br />
                                <div className="card">
                                    <div className="card-profile">
                                        <div className="people-card">
                                            {item?.schedule?.client?.avatar_url ? (
                                                <Avatar src={item?.schedule?.client?.avatar_url} style={{width: 40, height: 40}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(
                                                            item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name,
                                                        ),
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 20,
                                                    }}>
                                                    {getInitials(item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name)}
                                                </Avatar>
                                            )}
                                            <div className="people-profile-card">
                                                <Typography variant="h8" color="text.primary" className="name">
                                                    {item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name}
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary" className="name">
                                                    {item?.schedule?.service?.name}
                                                </Typography>
                                            </div>
                                        </div>
                                        {item?.status === 'scheduled' && (
                                            <Chip
                                                label="Scheduled"
                                                variant="filled"
                                                sx={theme => ({
                                                    backgroundColor: theme.palette.grey[700],
                                                    color: theme.palette.secondary.contrastText,
                                                })}
                                                size="small"
                                            />
                                        )}
                                        {item?.status === 'in_progress' && <Chip label="In progress" variant="filled" color="warning" size="small" />}
                                        {item?.status === 'ended' && (
                                            <Chip
                                                label="Ended"
                                                variant="filled"
                                                sx={theme => ({
                                                    backgroundColor: '#02CAD1',
                                                    color: theme.palette.primary.contrastText,
                                                })}
                                                size="small"
                                            />
                                        )}
                                        {item?.status === 'finished' && <Chip label="Finished" variant="filled" color="info" size="small" />}
                                        {item?.status === 'completed' && <Chip label="Completed" variant="filled" color="success" size="small" />}
                                        {item?.status === 'cancelled' && <Chip label="Cancelled" variant="filled" color="error" size="small" />}
                                    </div>
                                    <br />
                                    <div className="datetime">
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <DateRange fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                            </Typography>
                                        </div>
                                        <Typography variant="body3" color="text.secondary">
                                            |
                                        </Typography>
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <AccessTime fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                            </Typography>
                                        </div>
                                    </div>
                                    <br />
                                    <Typography variant="h8" color="text.primary">
                                        New Schedule
                                    </Typography>
                                    <br />
                                    <br />
                                    <Typography variant="inputLabel" color="text.secondary">
                                        Date
                                    </Typography>
                                    <Controller
                                        {...form.register(`items.${index}.date`)}
                                        control={form.control}
                                        render={({field}) => (
                                            <DatePicker
                                                placeholder="Enter your date"
                                                {...field}
                                                value={field.value ?? null}
                                                onChange={newValue => field.onChange(newValue)}
                                                enableAccessibleFieldDOMStructure={false}
                                                slots={{
                                                    textField: params => <TextField {...params} fullWidth size="small" />,
                                                }}
                                            />
                                        )}
                                    />
                                    <br />
                                    <br />
                                    <div className="form">
                                        <div className="form-left">
                                            <Typography variant="inputLabel" color="text.secondary">
                                                Start Time
                                            </Typography>
                                            <TextField type="time" size="small" fullWidth {...form.register(`items.${index}.start_time`)} />
                                        </div>
                                        <div className="form-right">
                                            <Typography variant="inputLabel" color="text.secondary">
                                                End Time
                                            </Typography>
                                            <TextField type="time" size="small" fullWidth {...form.register(`items.${index}.end_time`)} />
                                        </div>
                                    </div>
                                    <br />
                                </div>
                            </div>
                        ),
                    )}
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)} disabled={loading}>
                                Reschedule {items?.length > 1 ? `(${items?.length})` : ''}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
